import { useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useStreamAccumulator } from "./useStreamAccumulator";
import { captainQueryKeys } from "../api/query-keys";
import { streamChat } from "../api/streamChat";
import { fetchConversation } from "../api/useConversation";
import type { CaptainMessage, ToolCallParsed } from "../types";

const STORAGE_KEY = "captain:conversationId";
const ERROR_TRANSLATION_KEYS = new Map([
  ["Captain.BudgetUsedUp", "captain.errors.usedUp"],
  ["Captain.ClientNotConfigured", "captain.errors.clientNotConfigured"],
]);

function translateError(t: TFunction, code: string, message: string) {
  const key = ERROR_TRANSLATION_KEYS.get(code);
  return key ? t(key, message) : message;
}

function hydrateMessage(m: CaptainMessage): CaptainMessage {
  if (m.messageType === "tool_call") {
    try {
      const parsed = JSON.parse(m.content) as ToolCallParsed;
      return { ...m, toolCallParsed: parsed };
    } catch {
      return m;
    }
  }
  return { ...m, isError: m.status === "blocked" };
}

export function useCaptainChat(tenantId?: string | null, clientId?: string) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<CaptainMessage[]>([]);
  const [conversationId, persistConversationIdState] = useState<string | null>(
    () => sessionStorage.getItem(STORAGE_KEY),
  );
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const activeConversationRef = useRef<string | null>(conversationId);
  const streamingForRef = useRef<string | null>(null);

  const stream = useStreamAccumulator();

  const persistConversationId = useCallback((id: string | null) => {
    activeConversationRef.current = id;
    persistConversationIdState(id);
    if (id) sessionStorage.setItem(STORAGE_KEY, id);
    else sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  // Hydrate conversation from sessionStorage on mount
  const hasHydratedRef = useRef(false);
  useEffect(() => {
    if (hasHydratedRef.current || messages.length > 0) return;
    hasHydratedRef.current = true;

    const storedId = sessionStorage.getItem(STORAGE_KEY);
    if (!storedId) return;

    fetchConversation(storedId)
      .then((conversation) => setMessages(conversation.messages.map(hydrateMessage)))
      .catch(() => {
        sessionStorage.removeItem(STORAGE_KEY);
        persistConversationIdState(null);
      });
  }, [messages.length]);

  const invalidateConversations = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: captainQueryKeys.conversations() });
  }, [queryClient]);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || isStreaming) return;

      setError(null);
      setMessages((prev) => [
        ...prev,
        { role: "user", content: text, createdAt: new Date().toISOString() },
      ]);
      setIsStreaming(true);
      stream.reset();

      const controller = new AbortController();
      abortRef.current = controller;
      const isNew = conversationId === null;

      let streamConversationId: string | null = null;
      const isStale = () =>
        streamConversationId !== null &&
        activeConversationRef.current !== streamConversationId;

      try {
        await streamChat(
          {
            conversationId: conversationId ?? undefined,
            message: text,
            tenantId: tenantId ?? undefined,
            clientId,
          },
          {
            onStart: (id) => {
              streamConversationId = id;
              streamingForRef.current = id;
              persistConversationId(id);
              if (isNew) invalidateConversations();
            },
            onThinking: (chunk) => {
              if (!isStale()) stream.callbacks.onThinking(chunk);
            },
            onThinkingEnd: (durationSeconds) => {
              if (!isStale()) stream.callbacks.onThinkingEnd(durationSeconds);
            },
            onContent: (chunk) => {
              if (!isStale()) stream.callbacks.onContent(chunk);
            },
            onContentReplace: (content, _reason) => {
              if (!isStale()) stream.callbacks.onContentReplace(content);
            },
            onToolCallStart: (toolName, callId, category) => {
              if (!isStale()) stream.callbacks.onToolCallStart(toolName, callId, category);
            },
            onToolCallEnd: (toolName, callId, category, result) => {
              if (!isStale()) stream.callbacks.onToolCallEnd(toolName, callId, category, result);
            },
            onDone: (_pt, _ct, groundednessWarning) => {
              if (isStale()) {
                streamingForRef.current = null;
                setIsStreaming(false);
                return;
              }

              // Finalize: read current state via ref (not the stale closure capture)
              const s = stream.stateRef.current;
              const finalMessages: CaptainMessage[] = [];

              // Thinking message
              if (s.thinkingContent) {
                finalMessages.push({
                  role: "assistant",
                  content: s.thinkingContent,
                  messageType: "thinking",
                  thinkingDurationSeconds: s.thinkingDurationSeconds,
                  createdAt: new Date().toISOString(),
                });
              }

              // Tool call messages from phases
              for (const phase of s.phases) {
                for (const step of phase.steps) {
                  finalMessages.push({
                    role: "assistant",
                    content: "",
                    messageType: "tool_call",
                    toolCallParsed: {
                      name: step.rawName ?? step.name,
                      result: step.result ?? "",
                      category: step.toolCategory,
                    },
                    createdAt: new Date().toISOString(),
                  });
                }
              }

              // Display tool results
              for (const display of s.displays) {
                const displayResult = JSON.stringify(display.payload);
                finalMessages.push({
                  role: "assistant",
                  content: JSON.stringify({ name: "Display.display", result: displayResult }),
                  messageType: "tool_call",
                  toolCallParsed: {
                    name: "Display.display",
                    result: displayResult,
                    category: "display",
                  },
                  createdAt: new Date().toISOString(),
                });
              }

              // Text content
              if (s.textContent.trim()) {
                finalMessages.push({
                  role: "assistant",
                  content: s.groundednessReplacement ?? s.textContent,
                  status: groundednessWarning ? "groundedness_warning" : undefined,
                  createdAt: new Date().toISOString(),
                });
              }

              setMessages((prev) => [...prev, ...finalMessages]);
              setIsStreaming(false);
              streamingForRef.current = null;
              stream.reset();
              invalidateConversations();
            },
            onError: (code, message) => {
              if (isStale()) {
                streamingForRef.current = null;
                setIsStreaming(false);
                return;
              }
              setMessages((prev) => [
                ...prev,
                {
                  role: "assistant",
                  content: translateError(t, code, message),
                  createdAt: new Date().toISOString(),
                  isError: true,
                },
              ]);
              setIsStreaming(false);
              stream.reset();
            },
          },
          controller.signal,
        );
      } catch {
        if (!controller.signal.aborted) {
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: t("captain.connectionError", "Failed to connect to Captain. Please try again."),
              createdAt: new Date().toISOString(),
              isError: true,
            },
          ]);
          setIsStreaming(false);
          stream.reset();
        }
      }
    },
    [conversationId, isStreaming, persistConversationId, tenantId, clientId, invalidateConversations, stream, t],
  );

  const loadConversation = useCallback(
    (id: string, loadedMessages: CaptainMessage[]) => {
      persistConversationId(id);
      setMessages(loadedMessages.map(hydrateMessage));
      setError(null);
      stream.reset();
      setIsStreaming(false);
    },
    [persistConversationId, stream],
  );

  const stopGenerating = useCallback(() => {
    abortRef.current?.abort();
    const s = stream.stateRef.current;
    const finalMessages: CaptainMessage[] = [];

    if (s.thinkingContent) {
      finalMessages.push({
        role: "assistant",
        content: s.thinkingContent,
        messageType: "thinking",
        thinkingDurationSeconds: s.thinkingDurationSeconds,
        createdAt: new Date().toISOString(),
      });
    }

    for (const phase of s.phases) {
      for (const step of phase.steps) {
        finalMessages.push({
          role: "assistant",
          content: "",
          messageType: "tool_call",
          toolCallParsed: { name: step.rawName ?? step.name, result: step.result ?? "", category: step.toolCategory },
          createdAt: new Date().toISOString(),
        });
      }
    }

    for (const display of s.displays) {
      const displayResult = JSON.stringify(display.payload);
      finalMessages.push({
        role: "assistant",
        content: JSON.stringify({ name: "Display.display", result: displayResult }),
        messageType: "tool_call",
        toolCallParsed: { name: "Display.display", result: displayResult, category: "display" },
        createdAt: new Date().toISOString(),
      });
    }

    if (s.textContent.trim()) {
      finalMessages.push({
        role: "assistant",
        content: s.textContent,
        createdAt: new Date().toISOString(),
      });
    }

    if (finalMessages.length > 0) {
      setMessages((prev) => [...prev, ...finalMessages]);
    }

    setIsStreaming(false);
    streamingForRef.current = null;
    stream.reset();
  }, [stream]);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    persistConversationId(null);
    setMessages([]);
    stream.reset();
    setIsStreaming(false);
    setError(null);
  }, [persistConversationId, stream]);

  return {
    messages,
    conversationId,
    isStreaming,
    streamState: stream.state,
    error,
    sendMessage,
    stopGenerating,
    loadConversation,
    reset,
  };
}
